// src/components/ToastBanner.jsx
//
// Shared fixed-position dismissible toast banner.
// Originally extracted from SpaceHeadroomV2.jsx.
//
// TOAST-T1: Extended with bottom-right position, typed icons, auto-dismiss
// timer, optional action button, and ARIA live-region attributes.

import { useEffect, useRef } from "react";
import { LAYERS, T } from "../app/tokens.js";
import "./ToastBanner.css";

// ─── Type metadata ────────────────────────────────────────────────────────────

const TYPE_META = {
  success: { icon: "✓", color: T.green },
  info:    { icon: "i", color: T.accent },
  error:   { icon: "✕", color: T.red  },
};

function ToastProgress({ action, color, testId }) {
  // Mounted with a message key: freeze duration just like the timer's closure,
  // even if a caller changes action props without changing the message.
  const duration = useRef(action ? 6000 : 4000).current;
  return (
    <span
      aria-hidden="true"
      data-testid={`${testId}-progress`}
      style={{
        position: "absolute", left: 0, bottom: 0,
        width: "100%", height: 2, background: color,
        transformOrigin: "left", pointerEvents: "none",
        animation: `places-toast-countdown ${duration}ms linear forwards`,
      }}
    />
  );
}

// ─── ToastBanner ──────────────────────────────────────────────────────────────

export function ToastBanner({
  message,
  onDismiss,
  testId    = "toast-banner",
  type      = "info",
  action,     // optional { label, onClick }
  // offsetBottom allows ToastHost to stack multiple banners
  offsetBottom = 24,
}) {
  const meta        = TYPE_META[type] ?? TYPE_META.info;
  const dismissedRef = useRef(false);

  // Auto-dismiss: 6 000 ms when an action is present, 4 000 ms otherwise.
  useEffect(() => {
    if (!onDismiss) return;
    dismissedRef.current = false;
    const delay = action ? 6000 : 4000;
    const t = setTimeout(() => {
      if (!dismissedRef.current) onDismiss();
    }, delay);
    return () => clearTimeout(t);
  // Re-runs when message changes (new content resets the timer).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  function handleDismiss() {
    dismissedRef.current = true;
    if (onDismiss) onDismiss();
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className="places-toast"
      style={{
        position:  "fixed",
        bottom:    offsetBottom,
        right:     24,
        zIndex:    LAYERS.toast,
        background:   T.card,
        color:        T.dark,
        border:       `1px solid ${T.line}`,
        borderRadius: 12,
        padding:      "12px 15px",
        fontSize:     T.fsSm5,
        fontWeight:   T.fwSemibold,
        boxShadow:    T.shadowDropdown,
        boxSizing:    "border-box",
        overflow:     "hidden",
        display:      "flex",
        alignItems:   "center",
        gap:          12,
        whiteSpace:   "nowrap",
        maxWidth:     "calc(100vw - 48px)",
      }}
    >
      {/* Type icon */}
      <span
        aria-hidden="true"
        style={{
          background: meta.color, color: T.white, fontSize: T.fsMd,
          fontWeight: T.fwBold, flexShrink: 0, lineHeight: 1,
          width: 22, height: 22, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {meta.icon}
      </span>

      {/* Message */}
      <span style={{ flex: 1 }}>{message}</span>

      {/* Optional action button */}
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick?.(); handleDismiss(); }}
          style={{
            background:   "none",
            border:       "none",
            color:        meta.color,
            cursor:       "pointer",
            fontSize:     T.fsSm5,
            fontWeight:   T.fwBold,
            padding:      "0 4px",
            fontFamily:   "inherit",
            flexShrink:   0,
          }}
        >
          {action.label}
        </button>
      )}

      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        style={{
          background: "none",
          border:     "none",
          color:      T.mid,
          cursor:     "pointer",
          fontSize:   T.fsMd,
          padding:    0,
          flexShrink: 0,
          lineHeight: 1,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
      <ToastProgress key={message} action={action} color={meta.color} testId={testId} />
    </div>
  );
}
